


























































import Vue from 'vue'
import Background from './components/Background.vue'
import ConfigurationForm from './components/ConfigurationForm.vue'
import PipelineTile from './components/PipelineTile.vue'
import LoadingSpinner from './components/LoadingSpinner.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'
import storage from './lib/storage'
import GitLab from './clients/gitlab'
import {
  GitLabGroup,
  GitLabPipeline,
  GitLabProject,
  GitLabPipelineDetails,
  Config,
} from '@/interfaces'

interface Pipeline extends GitLabPipelineDetails {
  projectName: string,
  projectUrl: string
}

const defaultConfig = {
  url: 'https://gitlab.com',
  token: '_gHAG9yWN7Fx71GTKXMu',
  groups: 'yunyun_cloud/server;yunyun_cloud/firmware',
  projects: 'cubo_api;cubo-admin-api;cubo-api-utils;cubo_video_server;Cubo-Firmware-Update-Server;tutk_server',
}
const userConfig = storage.get('config') || {}

export default Vue.extend({
  name: 'app',
  components: {
    ConfigurationForm,
    PipelineTile,
    Background,
    LoadingSpinner,
    ErrorBoundary,
  },
  data: () => ({
    config: {
      ...defaultConfig,
      ...userConfig,
    } as Config,
    gitlab: null as null | GitLab,
    projects: null as null | GitLabProject[],
    pipelines: null as null | Pipeline[],
    pipelinePoll: null as any,
    projectPoll: null as any,
    retryPoll: null as any,
    showConfiguration: false as boolean,
    error: null as null | string | Error,
  }),
  computed: {
    configuring: function() {
      return this.showConfiguration ||
             !this.config.token ||
             !this.config.url ||
             (!this.config.groups && !this.config.projects)
    },
    loading: function() {
      return !this.pipelines
    },
  },
  watch: {
    async configuring(configuring: boolean) {
      if (!configuring) {
        this.pollProjects()
      }
    },
    async projects(projects: undefined | GitLabProject[]) {
      if (projects) {
        this.pollPipelines()
        this.stopPollingRetry()
      }
    },
  },
  methods: {
    retry() {
      this.error = null
      this.projects = null
      this.pollProjects()
    },
    async pollProjects() {
      this.stopPollingPipelines()
      await this.loadProjects(this.config)
      this.projectPoll = setInterval(
        () => this.loadProjects(this.config)
      , 60 * 1000)
    },
    async pollPipelines() {
      this.stopPollingPipelines()
      await this.loadPipelines()
      this.pipelinePoll = setInterval(
        () => this.loadPipelines()
      , 5 * 1000)
    },
    autoRetry() {
      this.retryPoll = setInterval(
        () => this.retry()
      , 60 * 1000)
    },
    stopPollingProjects() {
      if (this.projectPoll) {
        window.clearInterval(this.projectPoll)
      }
    },
    stopPollingPipelines() {
      if (this.pipelinePoll) {
        window.clearInterval(this.pipelinePoll)
      }
    },
    stopPollingRetry() {
      if (this.retryPoll) {
        window.clearInterval(this.retryPoll)
      }
    },
    openConfiguration() {
      this.showConfiguration = true
    },
    onSubmit(formConfig: Config) {
      this.stopPollingRetry()
      this.error = null
      this.pipelines = null
      this.projects = null
      this.showConfiguration = false
      this.config = formConfig
      storage.set('config', this.config)
    },
    splitNames(names: string) {
      return names.split(';')
        .map((part) => part.trim())
        .filter((part) => part !== '')
    },
    async loadProjects(config: Config) {
      try {
        this.gitlab = new GitLab(config.url, config.token)
        const groups = await this.getGroupsFromConfig(config.groups)
        const groupProjects = await this.getGroupsProjects(groups)
        const userProjects = await this.getProjectsFromConfig(config.projects)
        const projects = groupProjects.concat(userProjects).flatMap((a) => a)
        this.projects = projects
        if (projects.length === 0) {
          this.setError('Could not find any projects.')
        }
      } catch (err) {
        this.handleError(err)
      }
    },
    async loadPipelines() {
      if (this.projects) {
        this.pipelines = await this.getProjectsPipelines(this.projects)
      }
    },
    async getGroupsFromConfig(names: string) {
      const splitNames = this.splitNames(names)
      const groups = await Promise.all(splitNames.map((name) => {
        return this.gitlab!.searchGroups(name)
      }))
      return groups.flatMap((a) => a)
    },
    async getProjectsFromConfig(names: string) {
      const splitNames = this.splitNames(names)
      const projects = await Promise.all(splitNames.map((name) => {
        return this.gitlab!.searchProjects(name)
      }))
      return projects.flatMap((a) => a)
    },
    async getProjectsPipelines(projects: GitLabProject[]) {
      const batch = 5
      const pipelines = [] as Pipeline[]
      let i
      for (i = 0; i < projects.length; i += batch) {
        const projectPipelines = await Promise.all(projects.slice(i, i + batch).map((project) => {
          return this.gitlab!.getPipelines(project.id)
        }))
        let x
        for (x = 0; x < projectPipelines.length; x++) {
          const [pipeline] = projectPipelines[x]!
          if (!!pipeline) {
            const project = projects[i + x]
            const pipelineDetails = await this.gitlab!.getPipelineDetails(project.id, pipeline.id)
            if (!this.isPipelineZombie(pipelineDetails)) {
              pipelines.push({
                projectName: project.name,
                projectUrl: project.web_url,
                ...pipelineDetails,
              })
            }
          }
        }
      }
      return pipelines
    },
    async getGroupsProjects(groups: GitLabGroup[]) {
      const projects = await Promise.all(groups.map((group) => {
        return this.gitlab!.getAllProjects(group.id, { simple: true })
      }))
      return projects.flatMap((a) => a)
    },
    isPipelineZombie(pipeline: GitLabPipelineDetails) {
      return !pipeline.created_at && pipeline.status !== 'pending'
    },
    setError(err: string) {
      this.stopPollingPipelines()
      this.stopPollingProjects()
      this.error = err
    },
    handleError(err: any) {
      this.stopPollingPipelines()
      this.stopPollingProjects()
      this.autoRetry()
      switch (true) {
        case err.code && err.code === 'ECONNABORTED':
          return this.error = `Could not establish connection to '${this.config.url}'.`
        case err.response && (err.response.status === 401 || err.response.status === 403):
          return this.error = `Not authorized to get projects from '${this.config.url}'.`
        case err.response && err.response.status !== 200 || err.response.status !== 203:
          return this.error = `Could not get list of projects from '${this.config.url}'.`
        default:
          // tslint:disable-next-line:no-console
          console.error(err)
          return this.error = 'Something unexpected went wrong.'
      }
    },
    resizeGrid() {
      const tiles = this.$refs.tiles as HTMLDivElement
      if (!!tiles) {
        const grid = tiles.children[0] as HTMLDivElement
        const tile = grid.children[0] as HTMLDivElement
        const maxWidth = Math.floor(tiles.offsetWidth / tile.offsetWidth) * tile.offsetWidth
        grid.style.maxWidth = maxWidth + 'px'
      }
    },
  },
  created() {
    if (!this.configuring) {
      this.loadProjects(this.config)
    }
  },
  updated() {
    this.$nextTick(() => {
      this.resizeGrid()
    })
  },
  mounted() {
    window.addEventListener('resize', this.resizeGrid.bind(this))
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeGrid.bind(this))
  },
})
